/**
 * Simple comments section for news articles — public read, public post with name+body.
 * No auth required (matches other public comment surfaces on the platform).
 */
import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";

export default function NewsComments({ articleId }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await http.get(`/news/${articleId}/comments`);
      setItems(Array.isArray(data) ? data : []);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (articleId) load(); }, [articleId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || name.length < 1) return toast.error("Please enter your name");
    if (!body.trim() || body.length < 2) return toast.error("Please write a comment");
    setSubmitting(true);
    try {
      await http.post(`/news/${articleId}/comments`, { author_name: name.trim(), body: body.trim() });
      setBody("");
      toast.success("Comment posted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Post failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="news-comments" className="mt-12 border-t pt-8" style={{ borderColor: "var(--wofy-border)" }}>
      <div className="overline flex items-center gap-1.5 mb-2">
        <MessageCircle className="w-3 h-3" /> COMMENTS · {items.length}
      </div>
      <h3 className="font-display text-2xl mb-4">Join the conversation</h3>

      <form onSubmit={submit} className="wofy-card mb-6 space-y-3" data-testid="news-comment-form">
        <input
          data-testid="news-comment-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          maxLength={100}
          className="w-full px-3 py-2 rounded-lg border bg-transparent"
          style={{ borderColor: "var(--wofy-border)" }}
        />
        <textarea
          data-testid="news-comment-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Share your thoughts…"
          rows={3}
          maxLength={2000}
          className="w-full px-3 py-2 rounded-lg border bg-transparent"
          style={{ borderColor: "var(--wofy-border)" }}
        />
        <div className="flex justify-end">
          <button data-testid="news-comment-submit" type="submit" disabled={submitting} className="wofy-btn-primary text-sm">
            {submitting ? "Posting…" : "Post comment"}
          </button>
        </div>
      </form>

      {loading && <div className="text-sm opacity-60">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="text-sm italic" style={{ color: "var(--wofy-text-soft)" }}>Be the first to comment.</div>
      )}
      <div className="space-y-4">
        {items.map((c) => (
          <div key={c.id} data-testid={`news-comment-${c.id}`} className="wofy-card">
            <div className="flex items-center justify-between mb-1">
              <div className="font-display text-sm">{c.author_name}</div>
              <div className="text-[10px] font-mono opacity-50">{c.created_at?.slice(0, 16).replace("T", " ")}</div>
            </div>
            <p className="text-sm leading-relaxed whitespace-pre-line">{c.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
